import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Grid,
    Alert,
    CircularProgress,
    Tooltip,
    Pagination,
    Skeleton,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import useI18n from '../../../hooks/useI18n';
import bannersService from '../../../api/banners';
import priceListService from '../../../api/pricelist';
import ConfirmDialog from '../../custom/ConfirmDialog/ConfirmDialog';
import BannerPricelistPreview from '../../custom/BannerPricelistPreview/BannerPricelistPreview';
import ImageUploadBox from '../../custom/ImageUploadBox/ImageUploadBox';

function BannerSettings() {
    const { l, language } = useI18n();

    // State
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);

    // Dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        categories: [],
        brand: [],
        other: [],
        fish_type: [],
        fishing_type: [],
        search_strings: '',
        is_active: true,
        is_test: true,
        compno: 1,
    });

    // Image for create/edit banner - multilingual support
    const [bannerImageFiles, setBannerImageFiles] = useState({});
    const [currentEditLanguage, setCurrentEditLanguage] = useState('ua');

    // Unified filters state
    const [filtersConfig, setFiltersConfig] = useState([]);
    const [filterCombinations, setFilterCombinations] = useState([]);
    const [filtersLoading, setFiltersLoading] = useState(false);

    // Image upload
    const [selectedImage, setSelectedImage] = useState(null);
    
    // Multilingual images
    const [selectedLanguage, setSelectedLanguage] = useState('ua');
    const [languageImages, setLanguageImages] = useState({});
    const availableLanguages = ['ua', 'en', 'ru'];

    // Helper functions for filters
    const getLocalizedLabel = (labelObj) => {
        if (typeof labelObj === 'string') return labelObj;
        if (!labelObj) return '';
        const langKey = language === 'ru' ? 'ru' : language === 'en' ? 'en' : 'ua';
        return labelObj[langKey] || labelObj.ua || labelObj.en || '';
    };

    const getOptionValue = (option) => {
        if (typeof option === 'string') return option;
        // Handle multilingual objects {ua, en, ru}
        if (option && typeof option === 'object' && (option.ua || option.en || option.ru)) {
            return getLocalizedLabel(option);
        }
        return option?.value || option?.Attribute || '';
    };

    const getOptionLabel = (option) => {
        if (typeof option === 'string') return option;
        if (option?.label) {
            return getLocalizedLabel(option.label);
        }
        return option?.value || option?.Attribute || '';
    };

    // Calculate available options based on current selection
    const calculateAvailableOptions = (filterKey, currentSelection) => {
        if (!filterCombinations.length) return null;

        const levelKeys = ['LEVEL1', 'LEVEL2', 'LEVEL3'];
        const availableOptions = new Set();

        filterCombinations.forEach(combo => {
            // Get combo values with proper language (same as PriceList.js)
            const comboLevels = {
                LEVEL1: language === 'ru' ? combo.LEVEL1RUS : combo.LEVEL1,
                LEVEL2: language === 'ru' ? combo.LEVEL2RUS : combo.LEVEL2,
                LEVEL3: language === 'ru' ? combo.LEVEL3RUS : combo.LEVEL3
            };

            let matchesOtherFilters = true;

            for (const [key, values] of Object.entries(currentSelection)) {
                if (key === filterKey) continue;
                if (!values || values.length === 0) continue;

                const selectedValues = values.map(v => getOptionValue(v));

                if (levelKeys.includes(key)) {
                    if (!selectedValues.includes(comboLevels[key])) {
                        matchesOtherFilters = false;
                        break;
                    }
                } else {
                    const comboAttrsForType = (combo.attributeTypes && combo.attributeTypes[key]) || [];
                    const hasMatchingAttr = comboAttrsForType.some(attr =>
                        selectedValues.includes(attr)
                    );
                    if (!hasMatchingAttr) {
                        matchesOtherFilters = false;
                        break;
                    }
                }
            }

            if (matchesOtherFilters) {
                if (levelKeys.includes(filterKey)) {
                    const value = comboLevels[filterKey];
                    if (value) availableOptions.add(value);
                } else {
                    const comboAttrsForType = (combo.attributeTypes && combo.attributeTypes[filterKey]) || [];
                    comboAttrsForType.forEach(attr => {
                        availableOptions.add(attr);
                    });
                }
            }
        });

        return availableOptions;
    };

    const getFilteredOptions = (filter, currentSelection) => {
        const options = filter.options || [];

        const hasAnySelection = Object.values(currentSelection).some(v => v && v.length > 0);
        if (!filterCombinations.length || !hasAnySelection) {
            if (filter.key === 'LEVEL3') {
                return options.filter(opt => getOptionValue(opt) !== '');
            }
            return options;
        }

        const availableValues = calculateAvailableOptions(filter.key, currentSelection);
        if (!availableValues) return options;

        let filteredOptions = options.filter(opt => {
            const value = getOptionValue(opt);
            return availableValues.has(value);
        });

        if (filter.key === 'LEVEL3') {
            filteredOptions = filteredOptions.filter(opt => getOptionValue(opt) !== '');
        }

        return filteredOptions;
    };

    // Fetch unified filters
    const fetchUnifiedFilters = async (currentSelection = {}) => {
        try {
            setFiltersLoading(true);
            const result = await priceListService.getUnifiedFilters(currentSelection, language || 'ua');
            setFiltersConfig(result.filters || []);
            setFilterCombinations(result.filterCombinations || []);
        } catch (error) {
            console.error('Error fetchUnifiedFilters :>> ', error);
        } finally {
            setFiltersLoading(false);
        }
    };

    // Load banners
    useEffect(() => {
        loadBanners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const loadBanners = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await bannersService.getBannersList({ page, limit });
            setBanners(response.data || []);
            setTotal(response.total || 0);
        } catch (err) {
            setError(l('Failed to load banners') + ': ' + (err.message || ''));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (banner = null) => {
        if (banner) {
            setSelectedBanner(banner);
            setFormData({
                categories: banner.categories || [],
                brand: banner.brand || [],
                other: banner.other || [],
                fish_type: banner.fish_type || [],
                fishing_type: banner.fishing_type || [],
                search_strings: banner.search_strings || '',
                is_active: banner.is_active !== undefined ? banner.is_active : true,
                is_test: banner.is_test !== undefined ? banner.is_test : true,
                compno: banner.compno || 1,
            });
            // Встановлюємо існуючі зображення для мультимовності
            if (banner.image_url && typeof banner.image_url === 'object') {
                setLanguageImages(banner.image_url);
            } else {
                setLanguageImages({});
            }
        } else {
            setSelectedBanner(null);
            setFormData({
                categories: [],
                brand: [],
                other: [],
                fish_type: [],
                fishing_type: [],
                search_strings: '',
                is_active: true,
                is_test: true,
                compno: 1,
            });
            setLanguageImages({});
        }
        setBannerImageFiles({});
        setCurrentEditLanguage('ua');
        setOpenDialog(true);
        // Завантажуємо фільтри при відкритті діалогу
        fetchUnifiedFilters({});
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedBanner(null);
        setBannerImageFiles({});
        setLanguageImages({});
        setCurrentEditLanguage('ua');
        setError(null);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Mapping between filter keys and form data fields
    const getFormFieldForFilter = (filterKey) => {
        const mapping = {
            'LEVEL1': 'categories',
            'LEVEL2': 'brand',
            'LEVEL3': 'other',
            'fishes': 'fish_type',
            'types': 'fishing_type'
        };
        return mapping[filterKey] || filterKey.toLowerCase();
    };

    const getFilterKeyForFormField = (formField) => {
        const mapping = {
            'categories': 'LEVEL1',
            'brand': 'LEVEL2',
            'other': 'LEVEL3',
            'fish_type': 'fishes',
            'fishing_type': 'types'
        };
        return mapping[formField];
    };

    // Convert formData to filter selection for filtering
    const getFilterSelection = () => {
        const selection = {};
        Object.keys(formData).forEach(field => {
            if (field === 'search_strings' || field === 'is_active' || field === 'is_test' || field === 'compno') return;
            const filterKey = getFilterKeyForFormField(field);
            if (filterKey) {
                selection[filterKey] = formData[field] || [];
            }
        });
        return selection;
    };

    const renderFilter = (filter) => {
        const formField = getFormFieldForFilter(filter.key);
        const currentSelection = getFilterSelection();
        const options = getFilteredOptions(filter, currentSelection);


        // Ensure formData[formField] is always an array
        let formFieldValue = formData[formField] || [];
        if (!Array.isArray(formFieldValue)) {
            formFieldValue = [formFieldValue];
        }

        const value = formFieldValue.map(val => {
            // If val is a multilingual object, get its localized value
            const valStr = (val && typeof val === 'object' && (val.ua || val.en || val.ru)) 
                ? getLocalizedLabel(val) 
                : val;
            
            // Find matching option
            return options.find(opt => getOptionValue(opt) === valStr) || val;
        }).filter(Boolean);

        return (
            <Grid item xs={12} key={filter.key}>
                <Autocomplete
                    multiple
                    value={value}
                    onChange={(event, newValue) => {
                        const values = newValue.map(v => {
                            const val = getOptionValue(v);
                            return val;
                        });
                        setFormData(prev => ({ ...prev, [formField]: values }));
                    }}
                    options={options}
                    getOptionLabel={(option) => getOptionLabel(option)}
                    isOptionEqualToValue={(option, value) =>
                        getOptionValue(option) === getOptionValue(value)
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            label={getLocalizedLabel(filter.label)}
                            placeholder=""
                        />
                    )}
                />
            </Grid>
        );
    };

    const handleSaveBanner = async () => {
        setError(null);
        setSuccess(null);
        try {
            let bannerId;
            if (selectedBanner) {
                await bannersService.updateBanner(selectedBanner.id, formData);
                bannerId = selectedBanner.id;
                setSuccess(l('Banner updated successfully'));
            } else {
                const createdBanner = await bannersService.createBanner(formData);
                bannerId = createdBanner.id;
                setSuccess(l('Banner created successfully'));
            }
            
            // Завантажуємо нові зображення для всіх мов
            for (const [lang, file] of Object.entries(bannerImageFiles)) {
                if (file) {
                    await bannersService.uploadBannerImage(bannerId, file, lang);
                }
            }
            
            handleCloseDialog();
            loadBanners();
        } catch (err) {
            setError(l('Failed to save banner') + ': ' + (err.message || ''));
        }
    };

    const handleOpenImageDialog = (banner) => {
        setSelectedBanner(banner);
        setSelectedImage(null);
        setSelectedLanguage('ua');
        setLanguageImages(banner?.image_url || {});
        setOpenImageDialog(true);
    };

    const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
        setSelectedBanner(null);
        setSelectedImage(null);
        setSelectedLanguage('ua');
        setLanguageImages({});
    };

    const handleUploadImage = async () => {
        if (!selectedImage || !selectedBanner) return;
        
        setError(null);
        setSuccess(null);
        try {
            const result = await bannersService.uploadBannerImage(selectedBanner.id, selectedImage, selectedLanguage);
            setSuccess(l('Image uploaded successfully for') + ` ${selectedLanguage.toUpperCase()}`);
            setLanguageImages(result.banner.image_url || {});
            setSelectedImage(null);
            loadBanners();
        } catch (err) {
            setError(l('Failed to upload image') + ': ' + (err.message || ''));
        }
    };
    
    const handleDeleteLanguageImage = async (lang) => {
        if (!selectedBanner) return;
        
        setError(null);
        setSuccess(null);
        try {
            const result = await bannersService.deleteBannerImage(selectedBanner.id, lang);
            setSuccess(l('Image deleted successfully for') + ` ${lang.toUpperCase()}`);
            setLanguageImages(result.banner.image_url || {});
            loadBanners();
        } catch (err) {
            setError(l('Failed to delete image') + ': ' + (err.message || ''));
        }
    };

    const handleOpenDeleteDialog = (banner) => {
        setSelectedBanner(banner);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedBanner(null);
    };

    const handleDeleteBanner = async () => {
        if (!selectedBanner) return;
        
        setError(null);
        setSuccess(null);
        try {
            await bannersService.deleteBanner(selectedBanner.id);
            setSuccess(l('Banner deleted successfully'));
            handleCloseDeleteDialog();
            loadBanners();
        } catch (err) {
            setError(l('Failed to delete banner') + ': ' + (err.message || ''));
        }
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#333' }}>{l('Banner Settings')}</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    {l('Add Banner')}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{l('ID')}</TableCell>
                                        <TableCell>{l('Company')}</TableCell>
                                        <TableCell>{l('Image')}</TableCell>
                                        <TableCell>{l('Categories')}</TableCell>
                                        <TableCell>{l('Brands')}</TableCell>
                                        <TableCell>{l('Status')}</TableCell>
                                        <TableCell>{l('Mode')}</TableCell>
                                        <TableCell align="right">{l('Actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {banners.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {l('No banners found')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        banners.map((banner) => (
                                            <TableRow key={banner.id} hover>
                                                <TableCell>{banner.id}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={banner.compno || '-'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {banner.image_url && typeof banner.image_url === 'object' && Object.keys(banner.image_url).length > 0 ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box
                                                                component="img"
                                                                src={bannersService.getBannerImageUrl(banner.image_url, language || 'ua')}
                                                                alt="Banner"
                                                                sx={{
                                                                    width: 60,
                                                                    height: 40,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 1,
                                                                    border: '1px solid #e0e0e0',
                                                                }}
                                                            />
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                {Object.keys(banner.image_url).map(lang => (
                                                                    <Tooltip key={lang} title={`${lang.toUpperCase()} ${l('available')}`}>
                                                                        <Chip
                                                                            label={lang.toUpperCase()}
                                                                            size="small"
                                                                            sx={{ fontSize: '0.7rem', height: '20px' }}
                                                                        />
                                                                    </Tooltip>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    ) : banner.image_url && typeof banner.image_url === 'string' ? (
                                                        <Box
                                                            component="img"
                                                            src={bannersService.getBannerImageUrl(banner.image_url)}
                                                            alt="Banner"
                                                            sx={{
                                                                width: 60,
                                                                height: 40,
                                                                objectFit: 'cover',
                                                                borderRadius: 1,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<ImageIcon />}
                                                            label={l('No image')}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {banner.categories?.length > 0 ? (
                                                        <Chip
                                                            label={banner.categories.length}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {banner.brand?.length > 0 ? (
                                                        <Chip
                                                            label={banner.brand.length}
                                                            size="small"
                                                            color="secondary"
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={banner.is_active ? l('Active') : l('Inactive')}
                                                        color={banner.is_active ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={banner.is_test ? l('Test') : l('Production')}
                                                        color={banner.is_test ? 'warning' : 'info'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={l('Upload Image')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenImageDialog(banner)}
                                                            sx={{
                                                                color: '#F0B21C',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(240, 178, 28, 0.1)',
                                                                }
                                                            }}
                                                        >
                                                            <ImageIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={l('Edit')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDialog(banner)}
                                                            sx={{
                                                                color: '#F0B21C',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(240, 178, 28, 0.1)',
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={l('Delete')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDeleteDialog(banner)}
                                                            sx={{
                                                                color: '#d32f2f',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {total > limit && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <Pagination
                                    count={Math.ceil(total / limit)}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedBanner ? l('Edit Banner') : l('Create Banner')}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {filtersLoading ? (
                            // Show skeletons while filters are loading
                            <>
                                {[1, 2, 3, 4, 5].map((item) => (
                                    <Grid item xs={12} key={item}>
                                        <Skeleton variant="rectangular" width="100%" height={56} />
                                    </Grid>
                                ))}
                            </>
                        ) : (
                            // Render dynamic filters
                            filtersConfig.map(filter => renderFilter(filter))
                        )}
                        
                        {/* Search String - тепер звичайний текстовий рядок */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={l('Search String')}
                                value={formData.search_strings}
                                onChange={(e) => handleInputChange('search_strings', e.target.value)}
                                placeholder="Пошукова строка"
                            />
                        </Grid>

                        {/* Banner Image Upload - Multilingual */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {l('Banner Images')}
                            </Typography>
                            
                            {/* Language Selector */}
                            <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mr: 1 }}>
                                    {l('Select Language')}:
                                </Typography>
                                {availableLanguages.map((lang) => {
                                    const hasExistingImage = languageImages[lang];
                                    const hasNewImage = bannerImageFiles[lang];
                                    return (
                                        <Button
                                            key={lang}
                                            size="small"
                                            variant={currentEditLanguage === lang ? 'contained' : 'outlined'}
                                            onClick={() => setCurrentEditLanguage(lang)}
                                            sx={{
                                                minWidth: '70px',
                                                textTransform: 'uppercase',
                                                fontWeight: 600,
                                                position: 'relative',
                                            }}
                                        >
                                            {lang}
                                            {(hasExistingImage || hasNewImage) && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -4,
                                                        right: -4,
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: hasNewImage ? 'success.main' : 'info.main',
                                                    }}
                                                />
                                            )}
                                        </Button>
                                    );
                                })}
                            </Box>

                            {/* Current Images Status */}
                            {Object.keys(languageImages).length > 0 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {l('Existing images')}: {Object.keys(languageImages).map(lang => lang.toUpperCase()).join(', ')}
                                </Alert>
                            )}

                            {/* Image Upload for Current Language */}
                            <ImageUploadBox
                                key={currentEditLanguage}
                                onImageSelect={(file) => {
                                    setBannerImageFiles(prev => ({
                                        ...prev,
                                        [currentEditLanguage]: file
                                    }));
                                }}
                                initialPreview={
                                    bannerImageFiles[currentEditLanguage] 
                                        ? URL.createObjectURL(bannerImageFiles[currentEditLanguage])
                                        : languageImages[currentEditLanguage]
                                            ? bannersService.getBannerImageUrl(languageImages, currentEditLanguage)
                                            : null
                                }
                                accept="image/*"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {l('Upload image for')} {currentEditLanguage.toUpperCase()}. 
                                {languageImages[currentEditLanguage] && !bannerImageFiles[currentEditLanguage] && (
                                    <> {l('Current image will be kept if no new image is uploaded')}.</>
                                )}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                    />
                                }
                                label={l('Active')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_test}
                                        onChange={(e) => handleInputChange('is_test', e.target.checked)}
                                    />
                                }
                                label={l('Test Mode')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                    <Button 
                        onClick={handleCloseDialog}
                        sx={{
                            color: '#666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                        }}
                    >
                        {l('Cancel')}
                    </Button>
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={() => setOpenPreviewDialog(true)}
                        startIcon={<VisibilityIcon />}
                    >
                        {l('Show Pricelist')}
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleSaveBanner}
                        sx={{
                            boxShadow: '0 4px 12px rgba(240, 178, 28, 0.3)',
                        }}
                    >
                        {l('Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Upload Dialog - Multilingual */}
            <Dialog open={openImageDialog} onClose={handleCloseImageDialog} maxWidth="md" fullWidth>
                <DialogTitle>{l('Manage Banner Images')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {/* Language Selector */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {l('Select Language')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {availableLanguages.map((lang) => (
                                    <Button
                                        key={lang}
                                        variant={selectedLanguage === lang ? 'contained' : 'outlined'}
                                        onClick={() => {
                                            setSelectedLanguage(lang);
                                            setSelectedImage(null);
                                        }}
                                        sx={{
                                            minWidth: '80px',
                                            textTransform: 'uppercase',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {lang}
                                    </Button>
                                ))}
                            </Box>
                        </Box>

                        {/* Current Images Overview */}
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {l('Current Images')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {availableLanguages.map((lang) => (
                                    <Box key={lang} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            label={lang.toUpperCase()}
                                            color={languageImages[lang] ? 'success' : 'default'}
                                            size="small"
                                            icon={languageImages[lang] ? <ImageIcon /> : undefined}
                                        />
                                        {languageImages[lang] && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteLanguageImage(lang)}
                                                sx={{ color: 'error.main' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Image Upload Box for Selected Language */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {l('Upload Image for')} {selectedLanguage.toUpperCase()}
                            </Typography>
                            <ImageUploadBox
                                key={selectedLanguage}
                                onImageSelect={(file) => {
                                    setSelectedImage(file);
                                }}
                                initialPreview={
                                    languageImages[selectedLanguage] 
                                        ? bannersService.getBannerImageUrl(languageImages, selectedLanguage)
                                        : null
                                }
                                accept="image/*"
                            />
                        </Box>

                        {/* Preview of all images */}
                        {Object.keys(languageImages).length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                    {l('All Images Preview')}
                                </Typography>
                                <Grid container spacing={2}>
                                    {availableLanguages.map((lang) => {
                                        const imageUrl = bannersService.getBannerImageUrl(languageImages, lang);
                                        if (!imageUrl) return null;
                                        return (
                                            <Grid item xs={12} sm={4} key={lang}>
                                                <Paper sx={{ p: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                                                        {lang.toUpperCase()}
                                                    </Typography>
                                                    <Box
                                                        component="img"
                                                        src={imageUrl}
                                                        alt={`Banner ${lang}`}
                                                        sx={{
                                                            width: '100%',
                                                            height: 'auto',
                                                            borderRadius: 1,
                                                            border: '1px solid #e0e0e0',
                                                        }}
                                                    />
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                    <Button 
                        onClick={handleCloseImageDialog}
                        sx={{
                            color: '#666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                        }}
                    >
                        {l('Close')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUploadImage}
                        disabled={!selectedImage}
                        sx={{
                            boxShadow: '0 4px 12px rgba(240, 178, 28, 0.3)',
                        }}
                    >
                        {l('Upload for')} {selectedLanguage.toUpperCase()}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pricelist Preview Dialog */}
            <BannerPricelistPreview
                open={openPreviewDialog}
                onClose={() => setOpenPreviewDialog(false)}
                filters={formData}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={openDeleteDialog}
                handleClose={handleCloseDeleteDialog}
                handleConfirm={handleDeleteBanner}
                handleDeny={handleCloseDeleteDialog}
                title={l('Confirm Delete')}
                contentText={l('Are you sure you want to delete this banner? This action cannot be undone.')}
                denyButtonText={l('Cancel')}
                confirmButtonText={l('Delete')}
            />
        </Box>
    );
}

export default BannerSettings;
